'use client';

import { Avatar, Button, Modal, Popover, Spinner } from '@common/components';
import { SvgKebab } from '@common/icon';
import { cn } from '@core/utils';
import { useState } from 'react';

import { CommentForm } from './CommentForm';

interface CommentProps {
  profileUrl: string;
  nickname: string;
  daysAgo: string;
  mineYN: boolean;
  donationAmount?: number;
  comment: string;
  commentId: number;
  loading?: boolean;
  onUpdate?: (comment: string, commentId: number) => void;
  onDelete?: (commentId: number) => void;
}

export const Comment = ({
  profileUrl,
  nickname,
  daysAgo,
  mineYN,
  donationAmount,
  comment,
  commentId,
  onUpdate,
  onDelete,
  loading,
}: CommentProps) => {
  const [isEdit, setIsEdit] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleUpdate = () => {
    setIsEdit(true);
  };
  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  return (
    <article
      className={cn('relative flex flex-col', {
        'pointer-events-none blur-sm': loading,
      })}
    >
      <div className="flex gap-10 py-12">
        <Avatar alt="응원자 프로필 이미지" src={profileUrl} />
        <div className="flex flex-1 flex-col">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-8">
              <p className="font-500 text-Gray-800 typo-body4">{nickname}</p>
              {!isEdit && (
                <p className="font-400 text-Gray-600 typo-caption1">
                  {daysAgo}
                </p>
              )}
            </div>
            {!isEdit && mineYN && (
              <Popover.Root floatingPadding={8} placement="bottom-end">
                <Popover.Trigger className="w-fit">
                  <SvgKebab className="size-14 text-Gray-700" />
                </Popover.Trigger>
                <Popover.Content className="flex flex-col px-0 py-12 font-400 text-Gray-900 typo-body3">
                  <button
                    type="button"
                    className="w-118 px-16 py-8 text-left hover:bg-Gray-25"
                    onMouseDown={handleUpdate}
                  >
                    수정
                  </button>
                  <button
                    type="button"
                    className="w-118 px-16 py-8 text-left hover:bg-Gray-25"
                    onMouseDown={handleDelete}
                  >
                    삭제
                  </button>
                </Popover.Content>
              </Popover.Root>
            )}
          </div>
          {!isEdit && donationAmount && (
            <p className="font-700 text-Gray-900 typo-body4">
              {donationAmount?.toLocaleString()}원 참여
            </p>
          )}
          {!isEdit && (
            <p className="whitespace-pre-line pt-8 font-400 text-Gray-800 typo-body4">
              {comment}
            </p>
          )}
        </div>
      </div>
      {isEdit ? (
        <CommentForm
          onClose={() => setIsEdit(false)}
          initialComment={comment}
          isEdit
          onSubmit={(comment) => {
            onUpdate?.(comment, commentId);
            setIsEdit(false);
          }}
        />
      ) : (
        <></>
      )}
      {loading ? (
        <div className="absolute left-0 top-0 size-full bg-Gray-white/10 flex-center">
          <Spinner size={18} />
        </div>
      ) : (
        <></>
      )}
      <Modal
        opened={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        className="relative mt-0 w-280 min-w-[unset] overflow-hidden !p-0 lg:mt-0 lg:min-w-[unset]"
      >
        <div className="flex flex-col items-center px-20 pb-24 pt-32">
          <p className="pb-8 font-700 text-Gray-900 typo-body2">확인</p>
          <p className="pb-24 font-400 text-Gray-700 typo-body3">
            응원 댓글을 삭제하시겠어요?
          </p>
          <div className="flex w-full items-center gap-8">
            <Button
              theme="sub-fill"
              dimensions="H48"
              onClick={() => setIsDeleteModalOpen(false)}
              className="flex-1"
            >
              취소
            </Button>
            <Button
              theme="primary-fill"
              dimensions="H48"
              onClick={() => {
                onDelete?.(commentId);
                setIsDeleteModalOpen(false);
              }}
              className="flex-1"
            >
              삭제
            </Button>
          </div>
        </div>
      </Modal>
    </article>
  );
};
